package com.fireavert.react.pages.properties.breadcrumbs.property_details

import com.fireavert.cache.client.Preferences
import com.fireavert.logging.Logger
import com.fireavert.menu.menu_navigation.client.RoutePaths
import com.fireavert.properties.property_details.client.presentation.PropertyDetailsController
import com.fireavert.properties.property_details.client.presentation.PropertyDetailsViewModel
import com.fireavert.properties.shared.data_models.Mode
import com.fireavert.properties.shared.data_models.PmsType
import com.fireavert.react.auth.PermissionRequired
import com.fireavert.react.pages.help.HelpDialog
import com.fireavert.react.pages.properties.DamageEventsHappeningNow
import com.fireavert.react.pages.properties.GatewayDetails
import com.fireavert.react.pages.properties.ListUnitsAndDevices
import com.fireavert.react.pages.properties.breadcrumbs.property_details.dialogs.*
import com.fireavert.react.pages.properties.dialog.ArchivePropertyDialog
import com.fireavert.react.pages.properties.dialog.DeletePropertyAdminDialog
import com.fireavert.react.pages.properties.dialog.EditPropertyDialog
import com.fireavert.react.pages.properties.dialog.PropertyTestModeDialog
import com.fireavert.react.pages.properties.styles.PropertyStyles
import com.fireavert.react.shared.LoggedIn
import com.fireavert.react.shared.components.nav.NavOption
import com.fireavert.react.shared.components.nav.SideNavV2
import com.fireavert.react.shared.components.nav.TopNavBarV2
import com.fireavert.react.shared.styles.MobileStyles.MOBILE_VIEW_WIDTH
import com.fireavert.react.shared.styles.PageStyles
import com.fireavert.react.shared.styles.PageStylesV2
import com.fireavert.user.data_models.Permission
import com.fireavert.utilities.getKoinInstance
import emotion.css.ClassName
import emotion.css.cx
import emotion.react.css
import kotlinx.browser.sessionStorage
import kotlinx.browser.window
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import mui.material.*
import mui.material.styles.TypographyVariant
import mui.system.sx
import mui.system.useMediaQuery
import react.*
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.router.dom.Link
import react.router.dom.useSearchParams
import react.router.useParams
import web.cssom.*
import web.dom.Element
import web.html.HTMLElement

const val SESSION_STORAGE_SCROLL_KEY = "SESSION_STORAGE_SCROLL_KEY"
external interface PropertyDetailsProps : Props {
    var unitDetailsDialogIsOpen: Boolean
    var addNewUnitDialogIsOpen: Boolean
    var editPropertyDialogIsOpen: Boolean
    var editUnitDialogIsOpen: Boolean
    var deleteUnitDialogIsOpen: Boolean
    var dismissEventDialogIsOpen: Boolean
    var markAsTestDialogIsOpen: Boolean
    var markAsFalseAlarmDialogIsOpen: Boolean
    var rebootDeviceDialogIsOpen: Boolean
    var deletePropertyAdminDialogIsOpen: Boolean
    var rebootIsGas: Boolean
    var addNewUnitCSVDialogIsOpen: Boolean
    var convertToV2DialogIsOpen: Boolean
}


val PropertyDetailsV2 = FC<PropertyDetailsProps> { props ->
    val propertyIdParam = useParams()["propertyId"]?.toInt() ?: 0
    val queryStringParam = useSearchParams().toString()
    var propertyName: String by useState("")
    var propertyMode: Mode? by useState(null)
    var propertyTotalUnitsState: Int by useState(0)
    var pmsType: PmsType? by useState(null)
    val mainContentDivRef = useRef<Element>()
    var isAdmin: Boolean by useState(false)
    val isMobile = useMediaQuery(MOBILE_VIEW_WIDTH)
    val (openTestModeDialog, setTestModeOpenDialog) = useState(false)
    val (openArchivePropertyDialog, setArchivePropertyDialog) = useState(false)
    var testModeTime: Int by useState(0)
    var showBadPasswordAlert: Boolean by useState(false)
    var propertyCookingTime: Int? by useState(null)
    var isLoading: Boolean by useState(false)


    val viewModel = getKoinInstance<PropertyDetailsViewModel>()
    val controller = getKoinInstance<PropertyDetailsController>()
    val preferences = getKoinInstance<Preferences>()
    val logger = getKoinInstance<Logger>()

    fun getPmsIconPath(pmsType: PmsType?): String? {
        return when (pmsType) {
            PmsType.Yardi -> "/static/pms_icons/yardi_icon.svg"
            PmsType.RealPage -> "/static/pms_icons/realpage_icon.svg"
            PmsType.AppFolio -> "/static/pms_icons/appfolio_icon.svg"
            PmsType.Entrata -> "/static/pms_icons/entrata_icon.svg"
            PmsType.ResMan -> "/static/pms_icons/resman_icon.svg"
            PmsType.OneSite -> "/static/pms_icons/onesite_icon.svg"
            PmsType.MRI -> "/static/pms_icons/mri_icon.svg"
            PmsType.RentManager -> "/static/pms_icons/rentmanager_icon.svg"
            PmsType.None, null -> null
        }
    }

    fun saveScroll() {
        val element = mainContentDivRef.current as? HTMLElement
        element?.scrollTop?.let { scrollPosition ->
            sessionStorage.setItem(SESSION_STORAGE_SCROLL_KEY, scrollPosition.toString())
        }
    }

    fun restoreScroll() {
        window.requestAnimationFrame {
            val scrollRestore = sessionStorage.getItem(SESSION_STORAGE_SCROLL_KEY)?.toDouble() ?: 0.0
            val element = mainContentDivRef.current as? HTMLElement
            element?.scrollTo(0.0, scrollRestore)
        }
    }

    // Keep track of whether initial data has been loaded
    var dataLoaded by useState(false)
    useEffectOnceWithCleanup {
        val viewStateJob = viewModel.viewState.onEach { viewState ->
            propertyName = viewState.propertyName
            propertyTotalUnitsState = viewState.propertyTotalUnits
            dataLoaded = viewState.propertyName.isNotEmpty()
            propertyMode = viewState.propertyMode
            isAdmin = viewState.isAdmin
            isLoading = viewState.isLoading
            pmsType = viewState.pmsType
            propertyCookingTime = viewState.propertyDefaultStoveCookingTimeMinutes
        }.launchIn(viewModel.scope)

        val onLoadJob = viewModel.scope.launch {
            controller.onLoad(propertyIdParam, preferences.userId)
        }

        restoreScroll()
        onCleanup {
            onLoadJob.cancel()
            viewStateJob.cancel()
        }
    }

    useEffect(propertyIdParam) {
        viewModel.viewState.onEach { viewState ->
            propertyName = viewState.propertyName
            propertyTotalUnitsState = viewState.propertyTotalUnits
            dataLoaded = viewState.propertyName.isNotEmpty()
            propertyMode = viewState.propertyMode
            isAdmin = viewState.isAdmin
            isLoading = viewState.isLoading
            pmsType = viewState.pmsType
        }.launchIn(viewModel.scope)

        viewModel.scope.launch {
            controller.onLoad(propertyIdParam, preferences.userId)
        }
    }

    useEffect(props.unitDetailsDialogIsOpen) {
        if (!props.unitDetailsDialogIsOpen) {
            restoreScroll()
        }
    }

    LoggedIn {}
    if (props.editPropertyDialogIsOpen) {
        EditPropertyDialog {}
    } else if (props.deletePropertyAdminDialogIsOpen) {
        DeletePropertyAdminDialog {}
    } else if (props.unitDetailsDialogIsOpen) {
        if (isMobile) {
            UnitDetailsMobileDialog {
                this.isUserAdmin = isAdmin
            }
        }
        else {
            UnitDetailsDialog {
                this.isUserAdmin = isAdmin
            }
        }
    } else if (props.addNewUnitDialogIsOpen) {
        AddUnitDialog {
        }
    } else if (props.editUnitDialogIsOpen) {
        EditUnitDialog {
            this.propertyPmsType = pmsType ?: PmsType.None
            this.propertyDefaultCookingTime = propertyCookingTime ?: 0
        }
    } else if (props.addNewUnitCSVDialogIsOpen) {
        AddNewUnitsCSVDialog {}
    } else if (props.convertToV2DialogIsOpen) {
        ConvertToV2Dialog {}
    } else if (props.deleteUnitDialogIsOpen) {
        DeleteUnitDialog {}
    } else if (props.dismissEventDialogIsOpen) {
        DismissEventDialog {}
    } else if (props.markAsTestDialogIsOpen) {
        MarkAsTestDialog {}
    } else if (props.markAsFalseAlarmDialogIsOpen) {
        MarkAsFalseAlarmDialog {}
    } else if (props.rebootDeviceDialogIsOpen) {
        RebootDeviceDialog {
            isGas = props.rebootIsGas
        }
    }
    div {
        className = PageStyles.flexContainer
        SideNavV2 {
            activeNavOption = NavOption.Properties
        }
        div {
            className = cx(PageStylesV2.mainContainer, ClassName {
                backgroundColor = when (propertyMode) {
                    Mode.Active -> Color("#EDEFF3")
                    Mode.Test -> Color("#FF7E6B")
                    Mode.Install -> Color("#A9F0D1")
                    Mode.Archive -> Color("#e85a33")
                    else -> Color("#EDEFF3")
                }
            })

            onScroll = { _ ->
                saveScroll()
            }
            ref = mainContentDivRef
            TopNavBarV2 { activeNavOption = NavOption.Properties }
            div {
                className = PageStylesV2.mainContainerContent
                if (isMobile) {
                    div {
                        a {
                            href = RoutePaths.properties
                            +"< Properties"
                        }
                    }
                }

                div {
                    className = PageStyles.sectionTitleWithRightHandButton
                    div {
                        if (isLoading) {
                            // Property name skeleton
                            Skeleton {
                                variant = SkeletonVariant.text
                                width = 300.px
                                height = 48.px
                                sx { marginBottom = 8.px }
                            }
                        } else {
                            // Property name with PMS logo
                            div {
                                className = ClassName {
                                    display = Display.flex
                                    alignItems = AlignItems.center
                                    gap = 12.px
                                }

                                // PMS logo if available
                                getPmsIconPath(pmsType)?.let { iconPath ->
                                    Avatar {
                                        src = iconPath
                                        sx {
                                            width = 32.px
                                            height = 32.px
                                            backgroundColor = Color("#f5f5f5")
                                            padding = 4.px
                                        }
                                    }
                                }

                                // Property name
                                Typography {
                                    variant = TypographyVariant.h5
                                    sx {
                                        margin = 0.px
                                        fontWeight = FontWeight.bold
                                    }
                                    +propertyName
                                }
                            }
                        }
                        div {
                            if (isLoading) {
                                // Property mode badge skeleton
                                Skeleton {
                                    variant = SkeletonVariant.rectangular
                                    width = 100.px
                                    height = 24.px
                                    sx { borderRadius = 12.px }
                                }
                            } else {
                                when (propertyMode) {
                                    Mode.Active -> {
                                        className = PageStyles.test(NamedColor.green)
                                        Tooltip {
                                            title = HelpDialog.toolTipText(propertyMode.toString())
                                            placement = TooltipPlacement.top
                                            +"Protected"
                                        }
                                    }

                                    Mode.Test -> {
                                        className = PageStyles.test(NamedColor.black)
                                        Tooltip {
                                            title = HelpDialog.toolTipText(propertyMode.toString())
                                            placement = TooltipPlacement.top
                                            +"Test Mode"
                                        }
                                    }

                                    Mode.Install -> {
                                        className = PageStyles.test(NamedColor.black)
                                        Tooltip {
                                            title = HelpDialog.toolTipText(propertyMode.toString())
                                            placement = TooltipPlacement.top
                                            +"Install Mode"
                                        }
                                    }

                                    Mode.Mute -> {
                                        className = PageStyles.test(NamedColor.black)
                                        Tooltip {
                                            title = HelpDialog.toolTipText(propertyMode.toString())
                                            placement = TooltipPlacement.top
                                            +"Mute Mode"
                                        }
                                    }

                                    Mode.Archive -> {
                                        className = PageStyles.test(NamedColor.black)
                                        Tooltip {
                                            title = HelpDialog.toolTipText(propertyMode.toString())
                                            placement = TooltipPlacement.top
                                            +"Archived"
                                        }
                                    }

                                    null -> {
                                        className = PageStyles.test(NamedColor.black)
                                        Tooltip {
                                            title = HelpDialog.toolTipText(propertyMode.toString())
                                            placement = TooltipPlacement.top
                                            +"Loading..."
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if (openTestModeDialog) {
                        PropertyTestModeDialog {
                            closeCallback = {
                                setTestModeOpenDialog(false)
                                window.location.reload()
                            }
                            this.viewModel = viewModel
                            this.controller = controller
                            propertyId = propertyIdParam
                        }
                    }

                    if (openArchivePropertyDialog) {
                        ArchivePropertyDialog {
                            message =
                                "Are you sure you want to archive this property? No new data or notifications will work!"
                            error = if (showBadPasswordAlert) "Incorrect password, please try again." else ""
                            closeCallback = {
                                setArchivePropertyDialog(false)
                                window.location.reload()
                            }
                            archiveCallback = {
                                viewModel.scope.launch {
                                    val success = controller.archiveProperty(propertyIdParam, it)
                                    if (success) {
                                        showBadPasswordAlert = false
                                        setArchivePropertyDialog(false)
                                        window.location.reload()
                                    } else {
                                        showBadPasswordAlert = true
                                    }
                                }
                            }
                        }
                    }

                    div {
                        if (isLoading) {
                            // Buttons skeleton
                            div {
                                css {
                                    display = Display.flex
                                    gap = 16.px
                                    alignItems = AlignItems.center
                                }
                                Skeleton {
                                    variant = SkeletonVariant.rectangular
                                    width = 120.px
                                    height = 36.px
                                    sx { borderRadius = 4.px }
                                }
                                Skeleton {
                                    variant = SkeletonVariant.rectangular
                                    width = 140.px
                                    height = 36.px
                                    sx { borderRadius = 4.px }
                                }
                            }
                        } else {
                            if (!isMobile) {
                                PermissionRequired {
                                    permission = Permission.EditProperty
                                    fallback = null
                                    Link {
                                        to = "${RoutePaths.properties}/${propertyIdParam}/edit"
                                        Button {
                                            className = PageStylesV2.genericButton
                                            color = ButtonColor.primary
                                            variant = ButtonVariant.contained
                                            +"Edit Property"
                                        }
                                    }
                                }
                            }
                            if (propertyMode != Mode.Test) {
                                Button {
                                    variant = ButtonVariant.contained
                                    onClick = {
                                        setTestModeOpenDialog(true)
                                    }
                                    +"Turn on Test Mode"
                                }
                            } else if (isAdmin and isMobile) {
                                div {
                                    css {
                                        fontSize = 16.px
                                    }
                                    Link {
                                        to = "${RoutePaths.properties}/${propertyIdParam}/add_new_unit"
                                        a {
                                            +"Add Unit"
                                        }
                                    }
                                    +"  |  "
                                    Link {
                                        to = "${RoutePaths.properties}/${propertyIdParam}/edit"
                                        a {
                                            +"Edit Property"
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                // Damage Events Section
                if (isLoading) {
                    div {
                        css {
                            marginBottom = 24.px
                            padding = 16.px
                            backgroundColor = Color("#ffffff")
                            borderRadius = 8.px
                            border = Border(1.px, LineStyle.solid, Color("#e0e0e0"))
                        }
                        // Section title skeleton
                        Skeleton {
                            variant = SkeletonVariant.text
                            width = 200.px
                            height = 24.px
                            sx { marginBottom = 16.px }
                        }
                        // Event cards skeleton
                        div {
                            css {
                                display = Display.flex
                                flexDirection = FlexDirection.column
                                gap = 12.px
                            }
                            repeat(3) {
                                Skeleton {
                                    variant = SkeletonVariant.rectangular
                                    width = 100.pct
                                    height = 60.px
                                    sx { borderRadius = 4.px }
                                }
                            }
                        }
                    }
                } else {
                    DamageEventsHappeningNow {
                        propertyId = if (dataLoaded) propertyIdParam else 0
                        mobileVersion = isMobile
                    }
                }

                // Gateway Details Section
                if (isLoading) {
                    div {
                        css {
                            marginBottom = 24.px
                            padding = 16.px
                            backgroundColor = Color("#ffffff")
                            borderRadius = 8.px
                            border = Border(1.px, LineStyle.solid, Color("#e0e0e0"))
                        }
                        // Section title skeleton
                        Skeleton {
                            variant = SkeletonVariant.text
                            width = 180.px
                            height = 24.px
                            sx { marginBottom = 16.px }
                        }
                        // Gateway cards skeleton
                        div {
                            css {
                                display = Display.grid
                                gridTemplateColumns = if (isMobile) "1fr".unsafeCast<GridTemplateTracks>() else "repeat(auto-fit, minmax(300px, 1fr))".unsafeCast<GridTemplateTracks>()
                                gap = 16.px
                            }
                            repeat(2) {
                                Skeleton {
                                    variant = SkeletonVariant.rectangular
                                    width = 100.pct
                                    height = 120.px
                                    sx { borderRadius = 4.px }
                                }
                            }
                        }
                    }
                } else {
                    GatewayDetails {
                        propsIsAdmin = isAdmin
                        propsIsMobile = isMobile
                        propertyId = if (dataLoaded) propertyIdParam else 0
                        propsPropertyName = propertyName
                    }
                }

                // Units and Devices Section
                if (isLoading) {
                    div {
                        css {
                            marginBottom = 24.px
                            padding = 16.px
                            backgroundColor = Color("#ffffff")
                            borderRadius = 8.px
                            border = Border(1.px, LineStyle.solid, Color("#e0e0e0"))
                        }
                        // Section title and controls skeleton
                        div {
                            css {
                                display = Display.flex
                                justifyContent = JustifyContent.spaceBetween
                                alignItems = AlignItems.center
                                marginBottom = 16.px
                            }
                            Skeleton {
                                variant = SkeletonVariant.text
                                width = 150.px
                                height = 24.px
                            }
                            div {
                                css {
                                    display = Display.flex
                                    gap = 8.px
                                }
                                Skeleton {
                                    variant = SkeletonVariant.rectangular
                                    width = 80.px
                                    height = 32.px
                                    sx { borderRadius = 4.px }
                                }
                                Skeleton {
                                    variant = SkeletonVariant.rectangular
                                    width = 100.px
                                    height = 32.px
                                    sx { borderRadius = 4.px }
                                }
                            }
                        }
                        // Table skeleton
                        div {
                            css {
                                display = Display.flex
                                flexDirection = FlexDirection.column
                                gap = 8.px
                            }
                            // Table header skeleton
                            Skeleton {
                                variant = SkeletonVariant.rectangular
                                width = 100.pct
                                height = 40.px
                                sx { borderRadius = 4.px }
                            }
                            // Table rows skeleton
                            repeat(5) {
                                Skeleton {
                                    variant = SkeletonVariant.rectangular
                                    width = 100.pct
                                    height = 60.px
                                    sx { borderRadius = 4.px }
                                }
                            }
                        }
                    }
                } else {
                    ListUnitsAndDevices {
                        propertyId = if (dataLoaded) propertyIdParam else 0
                        propertyTotalUnitCount = propertyTotalUnitsState
                        queryString = queryStringParam
                        isMobileVersion = isMobile
                    }
                }
            }

            if (isAdmin && propertyMode != Mode.Archive) {
                div {
                    className = PropertyStyles.propertyArchiveButton
                    Button {
                        onClick = {
                            setArchivePropertyDialog(true)
                        }
                        variant = ButtonVariant.contained
                        css { backgroundColor = important(NamedColor.red) }
                        +"Archive Property"
                    }
                }
            }
        }
    }
}